"use client";

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";

type SidebarContextValue = {
  collapsed: boolean;
  toggle: () => void;
  setCollapsed: (v: boolean) => void;
  isMobile: boolean;
  mobileOpen: boolean;
  setMobileOpen: (v: boolean) => void;
  hoverExpanded: boolean;
  setHoverExpanded: (v: boolean) => void;
  /** True when sidebar should visually show labels (mobile, expanded, or hover-expanded) */
  showLabels: boolean;
  /** The visual width the sidebar should render at */
  visualWidth: number;
};

const SidebarContext = createContext<SidebarContextValue | null>(null);

const STORAGE_KEY = "cf-sidebar";
const EXPANDED = 220;
const COLLAPSED = 68;

export function SidebarProvider({ children }: { children: ReactNode }) {
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [hoverExpanded, setHoverExpanded] = useState(false);

  // Hydrate from localStorage after mount (avoids SSR mismatch)
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "true") setCollapsed(true);
  }, []);

  // Responsive: detect mobile viewport
  useEffect(() => {
    const mql = window.matchMedia("(max-width: 639px)");

    function handleChange(e: MediaQueryListEvent | MediaQueryList) {
      if (e.matches) {
        setIsMobile(true);
        setCollapsed(true);
        setMobileOpen(false);
      } else {
        setIsMobile(false);
      }
    }

    // Initial check
    handleChange(mql);

    mql.addEventListener("change", handleChange);
    return () => mql.removeEventListener("change", handleChange);
  }, []);

  // Persist on change (only on desktop)
  useEffect(() => {
    if (!isMobile) {
      localStorage.setItem(STORAGE_KEY, String(collapsed));
    }
  }, [collapsed, isMobile]);

  // Clear hover-expanded when sidebar is toggled to expanded
  useEffect(() => {
    if (!collapsed) setHoverExpanded(false);
  }, [collapsed]);

  const toggle = useCallback(() => setCollapsed((v) => !v), []);

  const showLabels = isMobile || !collapsed || hoverExpanded;
  const visualWidth = isMobile ? EXPANDED : (collapsed && !hoverExpanded) ? COLLAPSED : EXPANDED;

  return (
    <SidebarContext.Provider value={{
      collapsed, toggle, setCollapsed,
      isMobile, mobileOpen, setMobileOpen,
      hoverExpanded, setHoverExpanded,
      showLabels, visualWidth,
    }}>
      {children}
    </SidebarContext.Provider>
  );
}

export function useSidebar() {
  const ctx = useContext(SidebarContext);
  if (!ctx) throw new Error("useSidebar must be used within SidebarProvider");
  return ctx;
}
